import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getZonedTimestamp, getZonedCalendarDate, getBrowserTimeZone } from 'helpers/dateTimeHelper';
import { FREQUENCIES, MAX_INTERVAL, MAX_DAY_OF_MONTH, LAST_DAY_OF_MONTH, dayOfMonthOptions, ordinal, unitKey, parseNonNegativeInt } from 'helpers/recurrenceLimits';
import SegmentedToggle from './SegmentedToggle';

// The "every N <unit>" suffix follows the chosen frequency; the shared unitKey
// helper (recurrenceLimits.js) picks the singular or plural inspect:unit_* key.
var FREQUENCY_UNIT = { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' };

var DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// The Due choice is not a stored field: it is whether the rule names any hours at all.
var DUE_END_OF_DAY = 'endOfDay';
var DUE_SET_TIME = 'setTime';

var MONTHS = [];
for (var month = 1; month <= 12; month++) {
  MONTHS.push(month);
}

// Times of Day are stored as integer hours but shown in 12-hour clock with a
// meridiem, the same 'h:mm a' rendering EarlySubmissionEditor already uses. Only the
// label changes -- the stored value stays the hour number.
export function formatHour(hour) {
  return moment({ hour: hour }).format('h:mm a');
}

// Both day selects share MAX_DAY_OF_MONTH (recurrenceLimits.js): the API rejects a yearly start
// date above it with a scheduledRecurrenceStartDate error and a monthly day above it with a
// scheduledRecurrenceDaysOfMonth error, both rendered below.
function formatDayOfMonth(day, t) {
  return day === LAST_DAY_OF_MONTH ? t('inspect:recurrenceDayOfMonthLast') : ordinal(day);
}

var CadenceEditor = function (props) {
  var frequency = props.frequency || 'weekly';
  var interval = props.interval;
  var daysOfWeek = props.daysOfWeek || [];
  var hoursOfDay = props.hoursOfDay || [];
  var daysOfMonth = props.daysOfMonth || [];
  var startDate = props.startDate;
  var onChange = props.onChange;
  var t = props.t;
  var errors = props.errors || {};

  var handleFrequencyChange = function (e) {
    var next = e.target.value;
    // Only the frequency changes. The other slot arrays are kept, so a trip through Monthly
    // brings the weekly days back: the wire sends only the chosen frequency's array
    // (inspectionPatchHelper) and every coarseness read goes through effectiveHoursOfDay.
    var partial = { frequency: next };
    if (next === 'weekly' && !daysOfWeek.length) partial.daysOfWeek = ['mon'];
    if (next === 'monthly' && !daysOfMonth.length) partial.daysOfMonth = [1];
    if (next === 'yearly' && startDate) {
      var tz = props.timeZone || getBrowserTimeZone();
      var anchor = getZonedCalendarDate(startDate, tz);
      if (anchor.getDate() > MAX_DAY_OF_MONTH) {
        partial.startDate = getZonedTimestamp(
          new Date(anchor.getFullYear(), anchor.getMonth(), MAX_DAY_OF_MONTH), tz, 0, 0, 0);
      }
    }
    onChange(partial);
  };

  var handleIntervalChange = function (e) {
    var value = parseNonNegativeInt(e.target.value);
    if (value === null) {
      onChange({ interval: null });
      return;
    }
    var max = MAX_INTERVAL[frequency] || 1;
    if (value < 1) value = 1;
    if (value > max) value = max;
    onChange({ interval: value });
  };

  // Switching to a set time seeds 8 AM; switching back drops every named hour.
  var handleDueChange = function (next) {
    if (next === DUE_SET_TIME) {
      if (!hoursOfDay.length) onChange({ hoursOfDay: [8] });
      return;
    }
    onChange({ hoursOfDay: [] });
  };

  var handleDayOfWeekToggle = function (dayKey) {
    var isSelected = daysOfWeek.indexOf(dayKey) !== -1;
    var next = [];
    for (var i = 0; i < DAYS_OF_WEEK.length; i++) {
      var candidate = DAYS_OF_WEEK[i];
      var keep = candidate === dayKey ? !isSelected : daysOfWeek.indexOf(candidate) !== -1;
      if (keep) next.push(candidate);
    }
    // A weekly rule needs at least one day, so clearing the last one is a no-op.
    if (next.length === 0) return;
    onChange({ daysOfWeek: next });
  };

  var handleAddHour = function (e) {
    if (e.target.value === '') return;
    var hour = parseInt(e.target.value, 10);
    if (isNaN(hour) || hoursOfDay.indexOf(hour) !== -1) return;
    var next = hoursOfDay.concat([hour]);
    next.sort(function (a, b) {
      return a - b;
    });
    onChange({ hoursOfDay: next });
  };

  var handleRemoveHour = function (hour) {
    var next = [];
    for (var i = 0; i < hoursOfDay.length; i++) {
      if (hoursOfDay[i] !== hour) next.push(hoursOfDay[i]);
    }
    onChange({ hoursOfDay: next });
  };

  var handleDayOfMonthChange = function (e) {
    onChange({ daysOfMonth: [parseInt(e.target.value, 10)] });
  };

  // A yearly rule has no month field on the resource: it recurs on the month and
  // day of its start date, so the yearly selectors write startDate. setMonth
  // takes month and day together, which avoids the "Jan 31 plus one month"
  // rollover, and the day is clamped to MAX_DAY_OF_MONTH (see above).
  //
  // The month and day are read from, and written back as, the calendar date in
  // the RULE's zone, which is what InspectionRecurrenceEngine.cs derives the
  // yearly pattern from. Browser-local reads would show (and store) the
  // previous day whenever the browser sits west of the rule's zone.
  var timeZone = props.timeZone || getBrowserTimeZone();
  // Only the yearly selectors read these; skip the Intl work on every other
  // frequency's re-render (each keystroke in the interval box re-renders).
  var startDateObj = frequency === 'yearly' && startDate ? getZonedCalendarDate(startDate, timeZone) : new Date();
  var yearlyYear = startDateObj.getFullYear();
  var yearlyMonth = startDateObj.getMonth() + 1;
  var yearlyDay = startDateObj.getDate();

  // Deliberately does not touch timeZone (defect B7): the rule's zone is an
  // explicit, separately-edited field, not something the yearly picker's
  // month/day math gets to silently overwrite with the browser's zone.
  var emitYearly = function (nextMonth, nextDay) {
    var next = new Date(yearlyYear, nextMonth - 1, Math.min(nextDay, MAX_DAY_OF_MONTH));
    onChange({ startDate: getZonedTimestamp(next, timeZone, 0, 0, 0) });
  };

  var handleYearlyMonthChange = function (e) {
    emitYearly(parseInt(e.target.value, 10), yearlyDay);
  };

  var handleYearlyDayChange = function (e) {
    emitYearly(yearlyMonth, parseInt(e.target.value, 10));
  };

  var availableHours = [];
  for (var hour = 0; hour < 24; hour++) {
    if (hoursOfDay.indexOf(hour) === -1) availableHours.push(hour);
  }

  var yearlyDayOptions = dayOfMonthOptions(frequency === 'yearly' ? yearlyDay : null, false);

  var selectedDayOfMonth = daysOfMonth.length ? daysOfMonth[0] : null;

  // Singular at an interval of exactly one (including the unset/null state,
  // which behaves as one) -- plural otherwise.
  var unitCount = interval === null || interval === undefined ? 1 : interval;
  var intervalUnitKey = unitKey(FREQUENCY_UNIT[frequency], unitCount);

  return (
    <div className="inspect-cadence-editor">
      <div className="inspect-recurrence-grid-row">
        <label className="inspect-recurrence-grid-label" htmlFor="inspect-frequency">
          {t('inspect:recurrenceRepeatsLabel')}
        </label>
        <div className="inspect-recurrence-grid-control">
          <div className="inspect-recurrence-row">
            <select
              id="inspect-frequency"
              className={'inspect-recurrence-select' + (errors.scheduledRecurrenceFrequency ? ' has-error' : '')}
              value={frequency}
              onChange={handleFrequencyChange}
              aria-label={t('inspect:recurrenceRepeatsLabel')}
            >
              {FREQUENCIES.map(function (f) {
                return (
                  <option key={f} value={f}>
                    {t('inspect:frequency_' + f)}
                  </option>
                );
              })}
            </select>
            <span className="inspect-recurrence-every-label">{t('inspect:recurrenceEveryLower')}</span>
            <input
              type="number"
              className={'inspect-recurrence-number' + (errors.scheduledRecurrenceInterval ? ' has-error' : '')}
              value={interval === null || interval === undefined ? '' : interval}
              min="1"
              max={MAX_INTERVAL[frequency]}
              onChange={handleIntervalChange}
              aria-label={t('inspect:recurrenceEvery')}
            />
            <span className="inspect-recurrence-every-label">{t(intervalUnitKey)}</span>
          </div>
          {errors.scheduledRecurrenceFrequency && (
            <div className="slate-text-input-error">{errors.scheduledRecurrenceFrequency}</div>
          )}
          {errors.scheduledRecurrenceInterval && (
            <div className="slate-text-input-error">{errors.scheduledRecurrenceInterval}</div>
          )}
        </div>
      </div>

      {frequency === 'weekly' && (
        <div className="inspect-recurrence-grid-row">
          <span className="inspect-recurrence-grid-label">{t('inspect:recurrenceDaysOfWeekLabel')}</span>
          <div className="inspect-recurrence-grid-control">
            <div className="inspect-chip-row">
              {DAYS_OF_WEEK.map(function (d) {
                var isOn = daysOfWeek.indexOf(d) !== -1;
                return (
                  <button
                    key={d}
                    type="button"
                    className={'inspect-chip' + (isOn ? ' active' : '')}
                    aria-pressed={isOn}
                    onClick={function () {
                      handleDayOfWeekToggle(d);
                    }}
                  >
                    {t('inspect:dow_' + d)}
                  </button>
                );
              })}
            </div>
            {errors.scheduledRecurrenceDaysOfWeek && (
              <div className="slate-text-input-error">{errors.scheduledRecurrenceDaysOfWeek}</div>
            )}
          </div>
        </div>
      )}

      {frequency === 'monthly' && (
        <div className="inspect-recurrence-grid-row">
          <span className="inspect-recurrence-grid-label">{t('inspect:recurrenceDayOfMonthLabel')}</span>
          <div className="inspect-recurrence-grid-control">
            <select
              className={'inspect-recurrence-select' + (errors.scheduledRecurrenceDaysOfMonth ? ' has-error' : '')}
              value={selectedDayOfMonth === null ? '' : selectedDayOfMonth}
              onChange={handleDayOfMonthChange}
              aria-label={t('inspect:recurrenceDayOfMonthLabel')}
            >
              {selectedDayOfMonth === null && <option value="" disabled></option>}
              {dayOfMonthOptions(selectedDayOfMonth, true).map(function (d) {
                return (
                  <option key={d} value={d}>
                    {formatDayOfMonth(d, t)}
                  </option>
                );
              })}
            </select>
            {errors.scheduledRecurrenceDaysOfMonth && (
              <div className="slate-text-input-error">{errors.scheduledRecurrenceDaysOfMonth}</div>
            )}
          </div>
        </div>
      )}

      {frequency === 'yearly' && (
        <div className="inspect-recurrence-grid-row">
          <span className="inspect-recurrence-grid-label">{t('inspect:recurrenceYearlyOnLabel')}</span>
          <div className="inspect-recurrence-grid-control">
            <div className="inspect-recurrence-row">
              <select
                className="inspect-recurrence-select"
                value={yearlyMonth}
                onChange={handleYearlyMonthChange}
                aria-label={t('inspect:recurrenceMonthLabel')}
              >
                {MONTHS.map(function (m) {
                  return (
                    <option key={m} value={m}>
                      {t('inspect:month_' + m)}
                    </option>
                  );
                })}
              </select>
              <select
                className="inspect-recurrence-select"
                value={yearlyDay}
                onChange={handleYearlyDayChange}
                aria-label={t('inspect:recurrenceDayOfMonthLabel')}
              >
                {yearlyDayOptions.map(function (d) {
                  return (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  );
                })}
              </select>
            </div>
            {errors.scheduledRecurrenceStartDate && (
              <div className="slate-text-input-error">{errors.scheduledRecurrenceStartDate}</div>
            )}
            {errors.scheduledRecurrenceDaysOfMonth && (
              <div className="slate-text-input-error">{errors.scheduledRecurrenceDaysOfMonth}</div>
            )}
          </div>
        </div>
      )}

      {frequency === 'daily' && (
        <div className="inspect-recurrence-grid-row">
          <span className="inspect-recurrence-grid-label">{t('inspect:recurrenceDueLabel')}</span>
          <div className="inspect-recurrence-grid-control">
            <div className="inspect-recurrence-row">
              <SegmentedToggle
                inline
                options={[
                  { value: DUE_END_OF_DAY, label: t('inspect:recurrenceDueEndOfDay') },
                  { value: DUE_SET_TIME, label: t('inspect:recurrenceDueSetTime') },
                ]}
                value={hoursOfDay.length ? DUE_SET_TIME : DUE_END_OF_DAY}
                onChange={handleDueChange}
                ariaLabel={t('inspect:recurrenceDueLabel')}
              />
              {hoursOfDay.length > 0 && (
                <div className="inspect-chip-row">
                  {hoursOfDay.map(function (h) {
                    return (
                      <span key={h} className="inspect-chip active inspect-chip-removable">
                        {formatHour(h)}
                        <button
                          type="button"
                          className="inspect-chip-remove"
                          aria-label={t('inspect:recurrenceRemoveHour')}
                          onClick={function () {
                            handleRemoveHour(h);
                          }}
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </span>
                    );
                  })}
                  <select
                    className="inspect-recurrence-select"
                    value=""
                    onChange={handleAddHour}
                    aria-label={t('inspect:recurrenceAddHour')}
                  >
                    <option value="">{t('inspect:recurrenceAddHour')}</option>
                    {availableHours.map(function (h) {
                      return (
                        <option key={h} value={h}>
                          {formatHour(h)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
            {errors.scheduledRecurrenceHoursOfDay && (
              <div className="slate-text-input-error">{errors.scheduledRecurrenceHoursOfDay}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CadenceEditor.propTypes = {
  frequency: PropTypes.string,
  interval: PropTypes.number,
  daysOfWeek: PropTypes.array,
  hoursOfDay: PropTypes.array,
  daysOfMonth: PropTypes.array,
  startDate: PropTypes.number,
  timeZone: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default CadenceEditor;
